// Find "related stories" for a target article: cheap tag-overlap
// similarity over the in-memory manifest, no LLM, no network. Sized
// for the slide-out's bottom slot (top 5 by default).
//
// Why tag-overlap and not embeddings: the enrich service stamps every
// article with 3–7 LLM-generated tags; embeddings would be a more
// expressive signal but they're not in the manifest (per BUILD_PLAN
// §6.1 the embedding lives only on the server-side EnrichedArticle).
// Tag-overlap is good enough for the demo, costs O(N×T) per article
// open where N=manifest size and T=tags-per-article, and ships
// immediately.

import type { ManifestEntry } from "./types";

/** Default fan-out for the slide-out's "Related" rail. Picked so the rail
 *  fits comfortably under the article body on a portrait phone without
 *  pushing the CTA off the first screen. */
export const DEFAULT_RELATED_COUNT = 5;

export interface ScoredRelated {
  entry: ManifestEntry;
  /** Number of distinct shared tags between `target` and `entry`. */
  overlap: number;
}

/** Set-intersection size: the number of distinct tags that appear in
 *  BOTH `a` and `b`. Duplicates within either input collapse — the
 *  enrich prompt is non-deterministic and could repeat a tag; we
 *  shouldn't double-count that as overlap. Empty / missing tag lists
 *  yield 0. Pure. Kept as a standalone export for unit-test convenience
 *  even though `findRelatedArticles` uses the lower-level
 *  `intersectionWithSet` to avoid re-allocating the target set per
 *  pool entry. */
export function tagOverlap(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined,
): number {
  if (!a || !b || a.length === 0 || b.length === 0) return 0;
  return intersectionWithSet(new Set(a), b);
}

/** Count how many of `tags`'s distinct entries are present in
 *  `targetSet`. Private helper so `findRelatedArticles` can build the
 *  target's set once and re-use it across every pool iteration; that
 *  drops N×log T extra Set allocations from the hot path. */
function intersectionWithSet(
  targetSet: ReadonlySet<string>,
  tags: readonly string[] | undefined,
): number {
  if (!tags || tags.length === 0 || targetSet.size === 0) return 0;
  // Dedup the second arg via a Set too so duplicates within `tags`
  // don't double-count — same symmetry contract as `tagOverlap`.
  let n = 0;
  for (const t of new Set(tags)) {
    if (targetSet.has(t)) n += 1;
  }
  return n;
}

/** Find the top-N articles in `pool` most related to `target` by shared-
 *  tag count. Always excludes `target` itself — matched by both `id` AND
 *  `url` so a near-canonical duplicate that leaked past `dedup` doesn't
 *  surface the open article under a different id. Articles with zero
 *  overlap are excluded (a "related" rail of zero-overlap stories is
 *  just noise). Ties break by `published_at` desc (newer wins), then by
 *  base `score` desc so the rail's ordering matches the rest of the
 *  feed's bias. Pure — feed it the in-memory manifest. */
export function findRelatedArticles(
  target: ManifestEntry,
  pool: readonly ManifestEntry[],
  n: number = DEFAULT_RELATED_COUNT,
): ManifestEntry[] {
  // Build the target's tag-set ONCE and reuse it across the loop —
  // saves N Set allocations vs. calling `tagOverlap` per entry.
  const targetSet: ReadonlySet<string> = new Set(target.tags ?? []);
  const scored: ScoredRelated[] = [];
  for (const entry of pool) {
    if (entry.id === target.id) continue;
    if (entry.url && target.url && entry.url === target.url) continue;
    const overlap = intersectionWithSet(targetSet, entry.tags);
    if (overlap === 0) continue;
    scored.push({ entry, overlap });
  }
  scored.sort((a, b) => {
    if (b.overlap !== a.overlap) return b.overlap - a.overlap;
    if (b.entry.published_at !== a.entry.published_at) {
      return b.entry.published_at - a.entry.published_at;
    }
    return b.entry.score - a.entry.score;
  });
  return scored.slice(0, n).map((s) => s.entry);
}
